import { useState, useEffect } from 'react';
import type { LegalDoc, LegalDocVersion } from '@/mocks/legalCenter';
import { getActiveVersion } from '@/mocks/legalCenter';

interface EditorModalProps {
  doc: LegalDoc;
  onClose: () => void;
  onSave: (docId: number, newVersion: LegalDocVersion) => void;
}

export default function EditorModal({ doc, onClose, onSave }: EditorModalProps) {
  const activeVersion = getActiveVersion(doc);
  const [content, setContent] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setContent(activeVersion.content);
    setNotes('');
  }, [doc, activeVersion]);

  const handleSave = async () => {
    if (!content.trim()) return;
    setSaving(true);

    const newVersion: LegalDocVersion = {
      versionNumber: doc.currentVersion + 1,
      content: content.trim(),
      status: 'draft',
      createdAt: new Date().toISOString(),
      createdBy: 'Admin Global',
      publishedAt: null,
      notes: notes.trim() || 'Sin notas de versión.',
    };

    // Simulate save delay
    await new Promise((r) => setTimeout(r, 400));
    onSave(doc.id, newVersion);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>

      {/* Modal */}
      <div className="relative z-10 bg-background-50 rounded-2xl w-full max-w-3xl max-h-[90vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="shrink-0 flex items-center justify-between px-5 py-4 border-b border-background-200">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-background-100 flex items-center justify-center shrink-0">
              <i className={`${doc.icon} text-foreground-600 text-sm`}></i>
            </div>
            <div className="min-w-0">
              <h3 className="text-foreground-950 font-bold text-sm truncate">Editar: {doc.title}</h3>
              <p className="text-foreground-400 text-[10px]">
                Creando versión {doc.currentVersion + 1} a partir de v{activeVersion.versionNumber} (activa)
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center hover:bg-background-200 transition-colors cursor-pointer shrink-0"
          >
            <i className="ri-close-line text-foreground-500 text-sm"></i>
          </button>
        </div>

        {/* Editor */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <div>
            <label className="block text-foreground-400 text-[9px] font-bold uppercase tracking-wider mb-2">
              Contenido HTML
            </label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={18}
              className="w-full bg-background-100 border border-background-200 rounded-xl px-4 py-3 text-xs text-foreground-800 font-mono leading-relaxed resize-y outline-none focus:border-foreground-300 transition-colors"
              placeholder="Escribí el contenido del documento legal..."
            ></textarea>
            <p className="text-foreground-300 text-[9px] mt-1">
              Soporta etiquetas HTML básicas: h2, h3, p, strong, ul, li.
            </p>
          </div>

          <div>
            <label className="block text-foreground-400 text-[9px] font-bold uppercase tracking-wider mb-2">
              Notas de versión
            </label>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Ej: Actualización integral Q2 2026."
              className="w-full bg-background-100 border border-background-200 rounded-xl px-4 py-2.5 text-xs text-foreground-800 placeholder:text-foreground-300 outline-none focus:border-foreground-300 transition-colors"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="shrink-0 flex items-center justify-between px-5 py-4 border-t border-background-200 bg-background-100/50">
          <p className="text-foreground-400 text-[10px]">
            El borrador se guarda como v{doc.currentVersion + 1} · No se publica automáticamente.
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-xl border border-background-200 text-foreground-600 text-xs font-medium hover:bg-background-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={!content.trim() || saving}
              className="px-5 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
            >
              {saving ? (
                <>
                  <span className="w-3 h-3 border-2 border-background-50/30 border-t-background-50 rounded-full animate-spin"></span>
                  Guardando...
                </>
              ) : (
                <>
                  <i className="ri-save-line text-sm"></i>
                  Guardar borrador
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}